import type { ReactNode } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  CopyButton,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@tinct/web-components';
import { IconInfoCircle } from '@tabler/icons-react';

type ShownOnceWarningProps = {
  title: string;
  children: ReactNode;
};

/**
 * The banner above a secret the API returns once: what it is, and why it must be copied now. An
 * instruction, not an error, so the `important` alert the recovery codes use, not the destructive red.
 */
export const ShownOnceWarning = ({ title, children }: ShownOnceWarningProps) => (
  <Alert variant="important">
    <IconInfoCircle />
    <AlertTitle>{title}</AlertTitle>
    <AlertDescription>{children}</AlertDescription>
  </Alert>
);

type SecretFieldProps = {
  value: string;
  /** Shown above the field; without it, `ariaLabel` names the field. */
  label?: string;
  ariaLabel?: string;
  toastMessage: string;
};

/** A read-only value with its copy button. */
export const SecretField = ({
  value,
  label,
  ariaLabel,
  toastMessage,
}: SecretFieldProps) => {
  const field = (
    <InputGroup>
      <InputGroupInput
        readOnly
        value={value}
        aria-label={label ? undefined : ariaLabel}
        className="font-mono text-xs"
      />
      <InputGroupAddon align="inline-end">
        <CopyButton value={value} toastMessage={toastMessage} />
      </InputGroupAddon>
    </InputGroup>
  );

  if (!label) {
    return field;
  }
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{label}</span>
      {field}
    </div>
  );
};
